import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { ensureExists, exists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCreateCustomerAttributeGroupMutation } from '../hooks/useCreateCustomerAttributeGroupMutation'
import { useUpdateCustomerAttributeGroupMutation } from '../hooks/useUpdateCustomerAttributeGroupMutation'

const schema = z.object({ name: z.string().min(1) })
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const EditCustomerAttributeGroupDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { customerAttributeGroup, dialogType } = ensureExists(
    dialogInfo,
    t('assertion_failed'),
  )
  const operationLabel = React.useMemo(
    () => (dialogType === 'create' ? t('add') : t('update')),
    [dialogType, t],
  )

  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: { name: customerAttributeGroup?.name ?? '' },
    resolver,
  })
  const nameField = useFormField(control, 'name')

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const createCustomerAttributeGroupMutation =
    useCreateCustomerAttributeGroupMutation({
      onSuccess: (data) => {
        // 対象サーベイが見つからない場合などcreateCustomerAttributeGroupはidを返さない。作成できていないので記録しない。
        if (exists(data.createCustomerAttributeGroup?.id)) {
          logEvent('Create customer attribute group')
        }
        closeDialogWithRefresh()
      },
    })
  const updateCustomerAttributeGroupMutation =
    useUpdateCustomerAttributeGroupMutation({
      onSuccess: (data) => {
        // 対象グループが見つからない場合などupdateCustomerAttributeGroupはidを返さない。更新できていないので記録しない。
        if (exists(data.updateCustomerAttributeGroup?.id)) {
          logEvent('Edit customer attribute group')
        }
        closeDialogWithRefresh()
      },
    })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ name }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      if (dialogType === 'create') {
        createCustomerAttributeGroupMutation.mutate(name)
      } else if (dialogType === 'update') {
        updateCustomerAttributeGroupMutation.mutate({
          customerAttributeGroupId: customerAttributeGroup.id,
          name,
        })
      }
    },
    [
      customerAttributeGroup,
      createCustomerAttributeGroupMutation,
      dialogType,
      isDirty,
      resetDialogInfo,
      updateCustomerAttributeGroupMutation,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={
        createCustomerAttributeGroupMutation.isPending ||
        updateCustomerAttributeGroupMutation.isPending
      }
      mode={dialogType === 'create' ? 'create' : 'update'}
      title={t('customized_segments_group_dialogtype_create_add_update', {
        operation: operationLabel,
      })}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer label={t('customized_segments_group_name')}>
        <StyledInput autoFocus={true} width="100%" {...nameField} />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
